import { WindowTitle } from "@saleor/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useShop from "@saleor/hooks/useShop";
import useUser from "@saleor/hooks/useUser";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import useBrandUpdate from "@saleor/products/hooks/useBrandUpdate";
import useProductUserType from "@saleor/products/hooks/useProductUserType";
import useUserViewUpdate from "@saleor/products/hooks/useUserViewUpdate";
import { getProductAvailabilityVariables } from "@saleor/products/utils/handlers";
import {
  SearchProductTypes_search_edges,
  SearchProductTypesVariables
} from "@saleor/searches/types/SearchProductTypes";
import useBrandPrivateSearch from "@saleor/searches/useBrandPrivateSearch";
import useCategorySearch from "@saleor/searches/useCategorySearch";
import useCollectionSearch from "@saleor/searches/useCollectionSearch";
import useProductTagSearch from "@saleor/searches/useProductTagSearch";
import useProductTypeSearch from "@saleor/searches/useProductTypeSearch";
import { useTaxTypeList } from "@saleor/taxes/queries";
import createMetadataCreateHandler from "@saleor/utils/handlers/metadataCreateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import { useWarehouseList } from "@saleor/warehouses/queries";
import { warehouseAddPath } from "@saleor/warehouses/urls";
import { convertFromRaw } from "draft-js";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";

import { useAuth } from "../../auth/AuthProvider";
import { decimal, maybe, stringToFloat } from "../../misc";
import {
  ProductStatusEnum,
  ProductUserViewEnum,
  StoreTypeEnum
} from "../../types/globalTypes";
import ProductCreatePage from "../components/ProductCreatePage";
import { ProductCreateData } from "../components/ProductCreatePage/form";
import {
  useProductCreateMutation,
  useProductSetAvailabilityForPurchase
} from "../mutations";
import { productListUrl, productMainCreateUrl } from "../urls";
import getProductAddUrlByUserType from "../utils/getProductAddUrlByUserType";
import getProductUpdateUrlByUserType from "../utils/getProductUpdateUrlByUserType";
import {
  hasNoError,
  useProductCreateValidation
} from "../utils/productValidations";

function getUserAccessTypes(
  userType,
  productUser: ProductsUser,
  productTypeSearchResults: SearchProductTypes_search_edges[]
): StoreTypeEnum[] | [] {
  let userAccessTypes = [];

  if (!productUser.isKAMS && !productUser.isSuperUser) {
    userAccessTypes = userType.accesses.map(({ storeType }) => storeType);
  } else if (productUser.isSuperUser) {
    userAccessTypes = [
      StoreTypeEnum.FOOD_AND_BEVERAGES,
      StoreTypeEnum.GENERAL_MERCHANDISE,
      StoreTypeEnum.GROCERY
    ];
  } else if (productUser.isKAMS && productTypeSearchResults !== undefined) {
    userAccessTypes = productTypeSearchResults.map(edge => edge.node.storeType);
  } else {
    userAccessTypes = [];
  }

  return userAccessTypes;
}

function getProductTypeSearchVariables(
  productUser: ProductsUser,
  defaultSearchData: SearchProductTypesVariables,
  brand: string
) {
  let searchVariables = { ...defaultSearchData };

  if (productUser.isKAMS) {
    searchVariables = {
      ...defaultSearchData,
      brand
    };
  }

  return searchVariables;
}

function getStoreTypeByUserAccess(
  productUser: ProductsUser,
  userAccessTypes: StoreTypeEnum[],
  productTypeSearchResults: SearchProductTypes_search_edges[]
): StoreTypeEnum {
  let storeType;

  if (!productUser.isKAMS) {
    storeType = userAccessTypes.length > 0 && userAccessTypes[0];
  } else if (
    productUser.isKAMS &&
    productTypeSearchResults !== undefined &&
    productTypeSearchResults.length
  ) {
    storeType = productTypeSearchResults[0].node.storeType;
  }

  return storeType;
}

interface ProductCreateViewProps {
  params: {
    viewAs: ProductUserViewEnum;
    brand: string;
  };
}

export const ProductCreateView: React.FC<ProductCreateViewProps> = ({
  params
}) => {
  const {
    user: { userType }
  } = useAuth();
  const { user: userDetail } = useUser();
  const navigate = useNavigator();
  const notify = useNotifier();
  const shop = useShop();
  const intl = useIntl();
  const [validate, setValidator] = React.useState({
    basePrice: null,
    category: null,
    description: null,
    height: null,
    isSimpleProduct: false,
    length: null,
    name: null,
    productStatus: null,
    sku: null,
    weight: null,
    width: null
  });

  // Custom hook for determining if user is KAMS, superuser or regular user
  const productsUser = useProductUserType(params);

  // Hook for updating selected KAMS user view
  const [onUserViewChange, userView, userViews] = useUserViewUpdate(
    userDetail,
    params
  );

  const { result: brandsResult } = useBrandPrivateSearch({
    skip: !productsUser.isKAMS,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      accountManager: productsUser.id
    }
  });

  const brands = maybe(() => brandsResult?.data?.search?.edges, []).map(
    edge => edge.node
  );

  // Hook for updating of brands
  const [onBrandChange, brand, brandChoices] = useBrandUpdate(
    params,
    brands,
    brandsResult.loading
  );

  const {
    loadMore: loadMoreProductTypes,
    result: searchProductTypesOpts
  } = useProductTypeSearch({
    variables: getProductTypeSearchVariables(
      productsUser,
      DEFAULT_INITIAL_SEARCH_DATA,
      params.brand
    )
  });

  const userAccessTypes: StoreTypeEnum[] = getUserAccessTypes(
    userType,
    productsUser,
    searchProductTypesOpts.data?.search.edges
  );

  // Searching of categories by user access and type

  const storeTypeForCategorySearch = getStoreTypeByUserAccess(
    productsUser,
    userAccessTypes,
    searchProductTypesOpts.data?.search.edges
  );

  const {
    loadMore: loadMoreCategories,
    search: searchCategory,
    result: searchCategoryOpts
  } = useCategorySearch({
    skip: storeTypeForCategorySearch === undefined,
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      storeType: storeTypeForCategorySearch
    }
  });

  // const filteredCategories =
  //   searchCategoryOpts.data?.search && searchCategoryOpts.data?.search.edges
  //     ? searchCategoryOpts.data?.search.edges.filter(data =>
  //         userAccessTypes.includes(data.node.storeType)
  //       )
  //     : [];

  const {
    loadMore: loadMoreCollections,
    search: searchCollection,
    result: searchCollectionOpts
  } = useCollectionSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const {
    loadMore: loadMoreTags,
    search: searchTags,
    result: searchTagsOpts
  } = useProductTagSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const tags = maybe(() => searchTagsOpts.data.search.edges, []).map(
    edge => edge.node
  );

  const filteredProductTypes =
    searchProductTypesOpts.data?.search &&
    searchProductTypesOpts.data?.search.edges
      ? searchProductTypesOpts.data?.search.edges.filter(data =>
          userAccessTypes.includes(data.node.storeType)
        )
      : [];

  const warehouses = useWarehouseList({
    displayLoader: true,
    variables: {
      first: 50
    }
  });

  const isTrackInventory = userAccessTypes.includes(
    StoreTypeEnum.GENERAL_MERCHANDISE
  );

  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});
  const taxTypes = useTaxTypeList({});

  const handleBack = () => navigate(productListUrl());

  const [
    setProductAvailability,
    productAvailabilityOpts
  ] = useProductSetAvailabilityForPurchase({
    onCompleted: data => {
      const errors = data?.productSetAvailabilityForPurchase?.errors;
      if (errors?.length === 0) {
        const id = data.productSetAvailabilityForPurchase.product.id;
        navigate(getProductUpdateUrlByUserType(productsUser, id, params));
      }
    }
  });

  const [productCreate, productCreateOpts] = useProductCreateMutation({
    onCompleted: data => {
      if (data.productCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Product created"
          })
        });
      } else if (data.productCreate.errors.some(e => e.code === "INVALID")) {
        notify({
          status: "error",
          text: data.productCreate.errors[0].message
        });
      }
    }
  });

  const handleCreate = async (formData: ProductCreateData) => {
    let productStatusValue = null;
    if (formData.productStatus === "DRAFT") {
      productStatusValue = ProductStatusEnum.DRAFT;
    } else if (formData.productStatus === "READY") {
      productStatusValue = ProductStatusEnum.READY;
    } else if (formData.productStatus === "UNLISTED") {
      productStatusValue = ProductStatusEnum.UNLISTED;
    }

    const result = await productCreate({
      variables: {
        input: {
          availabilityTimeEnd: formData.availabilityTimeEnd || "00:00",
          availabilityTimeStart: formData.availabilityTimeStart || "00:00",
          basePrice: decimal(formData.basePrice),
          brand: formData.brand,
          brandLabel: formData.brandLabel,
          category: formData.category,
          chargeTaxes: formData.chargeTaxes,
          collections: formData.collections,
          description: convertFromRaw(formData.description).getPlainText(),
          descriptionJson: JSON.stringify(formData.description),
          height: stringToFloat(formData.height),
          isAvailabilityTime: formData.availabilityTimeEnd ? true : false,
          isPublished: formData.isPublished,
          isSimpleProduct: formData.isSimpleProduct,
          length: stringToFloat(formData.length),
          name: formData.name,
          productStatus: productStatusValue,
          productType: formData.productType?.id,
          publicationDate:
            formData.publicationDate !== "" ? formData.publicationDate : null,
          seo: {
            description: formData.seoDescription,
            title: formData.seoTitle
          },
          sku: formData.sku,
          slug: formData.slug,
          stocks: formData.stocks.map(stock => ({
            quantity: parseInt(stock.value, 10),
            warehouse: stock.id
          })),
          tags: maybe(() => formData.tags.map(tags => tags.label)),
          taxCode: formData.changeTaxCode ? formData.taxCode : undefined,
          trackInventory: formData.trackInventory,
          visibleInListings: formData.visibleInListings,
          weight: stringToFloat(formData.weight),
          width: stringToFloat(formData.width)
        }
      }
    });

    const productId = result?.data?.productCreate?.product?.id;

    if (productId) {
      const { isAvailableForPurchase, availableForPurchase } = formData;

      const variables = getProductAvailabilityVariables({
        availableForPurchase,
        isAvailableForPurchase,
        productId
      });

      setProductAvailability({
        variables
      });
    }

    return productId || null;
  };

  const handleSubmit = createMetadataCreateHandler(
    handleCreate,
    updateMetadata,
    updatePrivateMetadata
  );

  const handleValidation = data => {
    const validationResult = useProductCreateValidation(data);
    setValidator(validationResult);
    if (hasNoError(validationResult)) {
      handleSubmit(data);
    }
  };

  useEffect(() => {
    if (productsUser.isKAMS && productsUser.isSellerMaster && brand.value) {
      navigate(
        getProductAddUrlByUserType(
          productsUser,
          () =>
            productMainCreateUrl({
              brand: brand.value,
              viewAs: params.viewAs
            }),
          params
        )
      );
    }
  }, [brand.value]);

  return (
    <>
      <WindowTitle
        title={intl.formatMessage({
          defaultMessage: "Create Product",
          description: "window title"
        })}
      />
      <ProductCreatePage
        productsUser={productsUser}
        brand={brand.value}
        tags={tags}
        brands={brandChoices}
        brandLabel={brand.label}
        onBrandChange={onBrandChange}
        userView={userView.value}
        userViewLabel={userView.label}
        userViews={userViews}
        onUserViewChange={onUserViewChange}
        currency={shop?.defaultCurrency}
        isTrackInventory={isTrackInventory}
        categories={(searchCategoryOpts.data?.search.edges || []).map(
          edge => edge.node
        )}
        collections={(searchCollectionOpts.data?.search.edges || []).map(
          edge => edge.node
        )}
        disabled={productCreateOpts.loading || productAvailabilityOpts.loading}
        errors={productCreateOpts.data?.productCreate.errors || []}
        fetchCategories={searchCategory}
        fetchCollections={searchCollection}
        fetchProductTypes={null}
        header={intl.formatMessage({
          defaultMessage: "New Product",
          description: "page header"
        })}
        productTypes={filteredProductTypes.map(edge => edge.node)}
        onBack={handleBack}
        onSubmit={data => handleValidation(data)}
        onWarehouseConfigure={() => navigate(warehouseAddPath)}
        saveButtonBarState={productCreateOpts.status}
        fetchMoreCategories={{
          hasMore: searchCategoryOpts.data?.search.pageInfo.hasNextPage,
          loading: searchCategoryOpts.loading,
          onFetchMore: loadMoreCategories
        }}
        fetchTags={searchTags}
        fetchMoreCollections={{
          hasMore: searchCollectionOpts.data?.search.pageInfo.hasNextPage,
          loading: searchCollectionOpts.loading,
          onFetchMore: loadMoreCollections
        }}
        fetchMoreProductTypes={{
          hasMore: searchProductTypesOpts.data?.search.pageInfo.hasNextPage,
          loading: searchProductTypesOpts.loading,
          onFetchMore: loadMoreProductTypes
        }}
        fetchMoreTags={{
          hasMore: maybe(() => searchTagsOpts.data.search.pageInfo.hasNextPage),
          loading: searchTagsOpts.loading,
          onFetchMore: loadMoreTags
        }}
        warehouses={
          warehouses.data?.warehouses.edges.map(edge => edge.node) || []
        }
        taxTypes={taxTypes.data?.taxTypes || []}
        weightUnit={shop?.defaultWeightUnit}
        validation={validate}
      />
    </>
  );
};
export default ProductCreateView;
